import { User } from '@angular/fire/auth';

/**
 * Centralized utility for tracing authentication and signout events.
 * Used to investigate random logout/signout issues.
 *
 * All logs are prefixed with [AUTH_TRACE] for easy filtering in console.
 *
 * Set ENABLED to false to turn off all auth tracing logs.
 *
 * @example
 * AuthTracing.log('AuthService.signoutAndRedirect', {
 *   action: 'signout',
 *   redirectPath: '/account/login',
 *   reason: 'user_initiated'
 * });
 */
export class AuthTracing {
  /**
   * Master toggle for all auth tracing logs.
   * Set to false to disable all logging from this utility.
   */
  private static readonly ENABLED = true;

  private static readonly PREFIX = '[AUTH_TRACE]';

  /**
   * Log auth/signout events.
   *
   * @param location Where this was called from (e.g., 'AuthService.signout')
   * @param context Optional minimal context (e.g., { redirectPath: '/login' })
   */
  static log(location: string, context?: Record<string, any>): void {
    if (!this.ENABLED) {
      return;
    }

    const message = `${this.PREFIX} ${location}`;

    // Note: In production with minified code, stack traces are not readable.
    // Therefore, location strings should be descriptive of where the call originated.
    if (context) {
      console.error(message, context);
    } else {
      console.error(message);
    }
  }

  /**
   * Log auth events with token timing context (browserTime, expirationTime, issuedAtTime).
   * Delegates to log() after fetching token metadata from the Firebase user when available.
   */
  static async logWithTokenContext(
    location: string,
    user: User | null | undefined,
    context?: Record<string, any>,
  ): Promise<void> {
    const browserTime = new Date().toISOString();
    const tokenContext: Record<string, string | undefined> = { browserTime };

    if (user) {
      const { expirationTime, issuedAtTime } = await user.getIdTokenResult();
      tokenContext.expirationTime = expirationTime;
      tokenContext.issuedAtTime = issuedAtTime;
    }

    this.log(location, { ...context, ...tokenContext });
  }
}
